import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "@solidjs/router";
import {
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
} from "solid-js";
import { api } from "../api/client";
import { loadTemplates } from "../lib/data";
import type { CompletedWorkout, PlannedWorkout } from "../api/types";
import PageHero from "../components/PageHero";
import Section from "../components/tg/Section";
import WorkoutForm, { type WorkoutFormValue } from "../components/WorkoutForm";
import { t } from "../i18n";
import { resolvePerformedAt } from "../lib/datetime";
import { backFallbackFromState, goBack } from "../lib/nav";
import { buildRepeatInitial, todayISO } from "../lib/repeatWorkout";
import {
  maybeCelebrateStreakIncrease,
  showSessionCelebration,
} from "../lib/streakCelebration";
import { useAuth } from "../stores/auth";

interface Props {
  kind: "completed" | "planned";
  mode: "create" | "edit";
}

type LoadState = "idle" | "loading" | "ready" | "error";

type SeedResult = {
  initial: Partial<WorkoutFormValue>;
  repeated: boolean;
  sourceTitle: string | null;
  emptyLast: boolean;
  loadError?: boolean;
};

export default function WorkoutEditorPage(props: Props) {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const backFallback = () => backFallbackFromState(location.state, "/");

  // ── Edit mode: load existing workout ───────────────────
  const [editState, setEditState] = createSignal<LoadState>("idle");
  const [existing, setExisting] = createSignal<
    CompletedWorkout | PlannedWorkout | null
  >(null);

  createEffect(() => {
    if (props.mode !== "edit") {
      setEditState("idle");
      setExisting(null);
      return;
    }
    const id = params.id;
    const kind = props.kind;
    if (!id) {
      setExisting(null);
      setEditState("error");
      return;
    }

    setEditState("loading");
    setExisting(null);
    let cancelled = false;

    (async () => {
      try {
        const w =
          kind === "completed"
            ? await api.getCompleted(id)
            : await api.getPlanned(id);
        if (cancelled) return;
        setExisting(w);
        setEditState("ready");
      } catch {
        if (cancelled) return;
        setExisting(null);
        setEditState("error");
      }
    })();

    onCleanup(() => {
      cancelled = true;
    });
  });

  // ── Create mode: optional repeat seed ──────────────────
  const [seedState, setSeedState] = createSignal<LoadState>("idle");
  const [createSeed, setCreateSeed] = createSignal<SeedResult | null>(null);

  // Stable key so we don't re-fetch on every render
  const seedKey = createMemo(() => {
    if (props.mode !== "create") return "";
    return [
      (search.fromCompleted as string) || "",
      (search.fromPlanned as string) || "",
      (search.fromTemplate as string) || "",
      search.fromLast === "1" || search.fromLast === "true" ? "1" : "",
      (search.date as string) || todayISO(),
      search.copyNotes === "1" || search.copyNotes === "true" ? "1" : "",
    ].join("|");
  });

  createEffect(() => {
    const key = seedKey();
    if (props.mode !== "create" || !key) {
      setSeedState("idle");
      setCreateSeed(null);
      return;
    }

    const [
      fromCompleted,
      fromPlanned,
      fromTemplate,
      fromLastFlag,
      date,
      copyNotesFlag,
    ] = key.split("|");
    const fromLast = fromLastFlag === "1";
    const copyNotes = copyNotesFlag === "1";

    setSeedState("loading");
    let cancelled = false;

    (async () => {
      try {
        if (fromLast) {
          const list = await api.listCompleted();
          if (cancelled) return;
          const last = list[0];
          if (!last) {
            setCreateSeed({
              initial: { date },
              repeated: false,
              sourceTitle: null,
              emptyLast: true,
            });
          } else {
            setCreateSeed({
              initial: buildRepeatInitial(last, { date, copyNotes }),
              repeated: true,
              sourceTitle: last.title,
              emptyLast: false,
            });
          }
          setSeedState("ready");
          return;
        }

        if (fromCompleted) {
          const w = await api.getCompleted(fromCompleted);
          if (cancelled) return;
          setCreateSeed({
            initial: buildRepeatInitial(w, { date, copyNotes }),
            repeated: true,
            sourceTitle: w.title,
            emptyLast: false,
          });
          setSeedState("ready");
          return;
        }

        if (fromPlanned) {
          const w = await api.getPlanned(fromPlanned);
          if (cancelled) return;
          setCreateSeed({
            initial: buildRepeatInitial(w, { date, copyNotes }),
            repeated: true,
            sourceTitle: w.title,
            emptyLast: false,
          });
          setSeedState("ready");
          return;
        }

        if (fromTemplate) {
          const list = await loadTemplates();
          if (cancelled) return;
          const tpl = list.find((x) => x.id === fromTemplate);
          if (!tpl) {
            setCreateSeed({
              initial: { date },
              repeated: false,
              sourceTitle: null,
              emptyLast: false,
              loadError: true,
            });
          } else {
            setCreateSeed({
              initial: buildRepeatInitial(
                { title: tpl.name, notes: null, exercises: tpl.exercises },
                { date, copyNotes: true },
              ),
              repeated: true,
              sourceTitle: tpl.name,
              emptyLast: false,
            });
          }
          setSeedState("ready");
          return;
        }

        if (cancelled) return;
        setCreateSeed({
          initial: { date },
          repeated: false,
          sourceTitle: null,
          emptyLast: false,
        });
        setSeedState("ready");
      } catch {
        if (cancelled) return;
        setCreateSeed({
          initial: { date },
          repeated: false,
          sourceTitle: null,
          emptyLast: false,
          loadError: true,
        });
        setSeedState("ready");
      }
    })();

    onCleanup(() => {
      cancelled = true;
    });
  });

  async function onSubmit(value: WorkoutFormValue) {
    if (props.kind === "completed") {
      const body = {
        title: value.title,
        notes: value.notes || null,
        performedAt: resolvePerformedAt({
          date: value.date,
          finishedAt: value.finishedAt,
          startedAt: value.startedAt,
        }),
        durationMinutes: value.durationMinutes ?? null,
        startedAt: value.startedAt ?? null,
        finishedAt: value.finishedAt ?? null,
        exercises: value.exercises,
      };
      if (props.mode === "create") {
        await api.createCompleted(body);
      } else {
        await api.updateCompleted(params.id!, body);
      }
      const prevStreak = auth.profile()?.streak ?? 0;
      await auth.refreshProfile().catch(() => undefined);
      const grew = maybeCelebrateStreakIncrease(
        prevStreak,
        auth.profile()?.streak,
      );
      if (!grew && props.mode === "create") {
        showSessionCelebration(value.durationMinutes ?? 0, value.title);
      }
    } else {
      const body = {
        title: value.title,
        notes: value.notes || null,
        plannedFor: value.date,
        startTime: value.startTime ?? null,
        exercises: value.exercises,
      };
      if (props.mode === "create") {
        await api.createPlanned(body);
      } else {
        await api.updatePlanned(params.id!, body);
      }
    }
    goBack(navigate, backFallback());
  }

  const editInitial = createMemo((): Partial<WorkoutFormValue> | undefined => {
    if (props.mode !== "edit") return undefined;
    const w = existing();
    if (!w) return undefined;
    if (props.kind === "completed" && "performedAt" in w) {
      return {
        title: w.title,
        notes: w.notes ?? "",
        date: w.performedAt.slice(0, 10),
        durationMinutes: w.durationMinutes ?? null,
        startedAt: w.startedAt ?? null,
        finishedAt: w.finishedAt ?? null,
        exercises: w.exercises,
      };
    }
    if (props.kind === "planned" && "plannedFor" in w) {
      return {
        title: w.title,
        notes: w.notes ?? "",
        date: w.plannedFor,
        startTime: w.startTime ?? null,
        exercises: w.exercises,
      };
    }
    return undefined;
  });

  const pageTitle = () => {
    if (props.mode === "create" && search.fromTemplate) {
      return t("FROM_TEMPLATE");
    }
    if (props.mode === "create" && createSeed()?.repeated) {
      return t("REPEAT_WORKOUT");
    }
    if (props.mode === "create") {
      return props.kind === "completed" ? t("NEW_COMPLETED") : t("NEW_PLANNED");
    }
    return t("EDIT");
  };

  const editMissing = () => props.mode === "edit" && editState() === "error";
  const editLoading = () => props.mode === "edit" && editState() === "loading";
  const createLoading = () =>
    props.mode === "create" && seedState() === "loading";
  const formReady = () => {
    if (props.mode === "edit") return editState() === "ready" && existing();
    return seedState() === "ready" && createSeed() != null;
  };

  function back() {
    if (editMissing()) {
      navigate(backFallback(), { replace: true });
      return;
    }
    goBack(navigate, backFallback());
  }

  return (
    <div>
      <PageHero
        title={editMissing() ? t("WORKOUT_NOT_FOUND") : pageTitle()}
        aside={
          <button type="button" class="btn-ghost px-2" onClick={back}>
            {t("BACK")}
          </button>
        }
      />

      <Show when={editMissing()}>
        <Section>
          <div class="px-4 py-4">
            <p class="text-[17px] font-medium text-[var(--destructive)]">
              {t("WORKOUT_NOT_FOUND")}
            </p>
            <p class="mt-1 text-[15px] text-ink-muted">
              {t("WORKOUT_NOT_FOUND_HINT")}
            </p>
          </div>
        </Section>
        <button
          type="button"
          class="btn-primary w-full"
          onClick={() => navigate(backFallback(), { replace: true })}
        >
          {t("BACK")}
        </button>
      </Show>

      <Show when={createSeed()?.emptyLast}>
        <p class="mb-3 px-1 text-[15px] text-[var(--destructive)]">
          {t("NO_WORKOUT_TO_REPEAT")}
        </p>
      </Show>

      <Show when={createSeed()?.loadError}>
        <p class="mb-3 px-1 text-[15px] text-[var(--destructive)]">
          {t("ERROR_GENERIC")}
        </p>
      </Show>

      <Show when={createSeed()?.repeated && createSeed()?.sourceTitle}>
        <Section footer={t("REPEAT_HINT")}>
          <div class="px-4 py-3 text-[15px]">
            {t("REPEAT_FROM")}: {createSeed()!.sourceTitle}
          </div>
        </Section>
      </Show>

      <Show when={editLoading() || createLoading()}>
        <p class="text-[15px] text-ink-muted">{t("LOADING")}</p>
      </Show>

      <Show when={formReady()}>
        <Show when={props.mode === "edit" && existing()}>
          <WorkoutForm
            mode={props.kind}
            initial={editInitial()}
            onSubmit={onSubmit}
            onCancel={back}
          />
        </Show>
        <Show when={props.mode === "create" && createSeed()}>
          <WorkoutForm
            mode={props.kind}
            initial={createSeed()!.initial}
            onSubmit={onSubmit}
            onCancel={back}
            submitLabel={
              createSeed()?.repeated
                ? props.kind === "completed"
                  ? t("SAVE_AS_COMPLETED")
                  : t("SAVE_AS_PLANNED")
                : undefined
            }
          />
        </Show>
      </Show>
    </div>
  );
}
